import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import styles from "./styles.module.scss";
import SelectBox from "app/components/OptionButtonGroup/SelectBox";
import Label from "app/components/Programs/Label";
import ButtonVariant from "app/components/Button/ButtonVariant";

type BookingOption = {
  id: number;
  title: string;
  duration: string;
  category?: string;
  quantity?: number;
};

type BookingModalProps = {
  isOpen: boolean;
  bookingOptions: BookingOption[];
  setBookingOptions: (opts: BookingOption[]) => void;
  onDismiss: () => void;
  lessonRates: BookingOption[];
};

export default function BookingModal({
  isOpen,
  bookingOptions,
  setBookingOptions,
  onDismiss,
  lessonRates
}: BookingModalProps) {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<BookingOption[]>(
    bookingOptions.map(o => ({ ...o, quantity: o.quantity ?? 1 }))
  );
  const [selectedOption, setSelectedOption] = useState<null | { value: number; label: string }>(null);

  const selectOptions = useMemo(() => lessonRates.map((rate) => ({
    value: rate.id,
    label: `${rate.title} (${rate.duration})`,
  })), [lessonRates]);

  const handleSelect = (selected: { value: number; label: string } | null) => {
    if (!selected) { return; }
    const option = lessonRates.find((opt) => opt.id === selected.value);
    if (!option) { return; }

    const already = selectedOptions.some(o => o.id === option.id);
    if (!already) {
      setSelectedOptions(prev => [...prev, { ...option, quantity: 1 }]);
    }
    setSelectedOption(null);
  };

  const handleRemove = (name: string) => {
    setSelectedOptions(selectedOptions.filter((opt) => opt.title !== name));
  };

  const handleAdd = () => {
    const normalized = selectedOptions.map(o => ({ ...o, quantity: o.quantity ?? 1 }));
    setBookingOptions(normalized);
    onDismiss();
  };

  const handleClose = () => {
    setSelectedOptions(bookingOptions);
    onDismiss();
  };


  return (
    <ModalLayout isOpen={isOpen} onClose={onDismiss} classNames={styles.compact_modal} centerVertically>
      <div className={styles.modal_header_row}>
        <div className={styles.title_modal}>{t("Add Options")}</div>
        <button className={styles.close_button} onClick={handleClose}>
          <i className="ico ico-x" />
        </button>
      </div>

      <div className={styles.field_container}>
        <Label
          text={t("Booking Options")}
          item={(
            <SelectBox
              options={selectOptions}
              value={selectedOption}
              setValue={handleSelect}
              noOptionsText={t("No booking options with online support available")}
              customWidth="100%"
            />
          )}
        />

      </div>

      <div className={styles.selected_list}>
        {selectedOptions.map((opt, index) => (
          <div key={opt.title}>
            <div className={styles.selected_item}>
              <div className={styles.option_text}>
                <strong>{opt.title}</strong>
                <div className={styles.duration}>{opt.duration}</div>
              </div>
              <button className={styles.remove_icon} onClick={() => handleRemove(opt.title)}>
                <i className="ico ico-x" />
              </button>
            </div>
            {index < selectedOptions.length - 1 && <hr className={styles.separator} />}
          </div>
        ))}
      </div>

      <div className={styles.action_row}>
        <ButtonVariant
          title={t("Cancel")}
          buttonType='secondary-outline'
          onClickFunc={handleClose}
          buttonStyles={styles.cancel_button}
          childStyles={styles.cancel_button_text}
        />
        <ButtonVariant
          title={t("Add")}
          buttonType='primary'
          onClickFunc={handleAdd}
          disabled={selectedOptions.length === 0}
          buttonStyles={styles.confirm_button}
        />
      </div>
    </ModalLayout>
  );
}
